import * as React from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { Input, type InputProps } from '@/components/ui/input';
import { cn } from '@/lib/utils';

export interface PasswordInputProps extends Omit<InputProps, 'type'> {
  /**
   * Base noun used in the toggle's aria-label: `Show ${toggleLabel}` / `Hide ${toggleLabel}`.
   * Defaults to 'password'. Pass e.g. 'confirm password' for a confirm field so screen
   * readers (and tests) can tell the two toggles apart.
   */
  toggleLabel?: string;
}

/**
 * Password field with a built-in show/hide (Eye) toggle.
 *
 * Wraps the shared `Input` in a `relative` container and overlays a reveal button, matching
 * the established pattern from RegisterPage/ResetPasswordPage. Visibility state is managed
 * internally, so callers only supply the usual input props. `className`, `ref`, and any extra
 * props (`aria-describedby`, `onBlur`, `disabled`, `autoComplete`, ...) pass straight through
 * to the underlying input; `pr-10` is added automatically to leave room for the icon.
 */
const PasswordInput = React.forwardRef<HTMLInputElement, PasswordInputProps>(
  ({ className, toggleLabel = 'password', disabled, ...props }, ref) => {
    const [visible, setVisible] = React.useState(false);

    return (
      <div className="relative">
        <Input
          ref={ref}
          type={visible ? 'text' : 'password'}
          disabled={disabled}
          className={cn('pr-10', className)}
          {...props}
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          disabled={disabled}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 disabled:opacity-50"
          aria-label={visible ? `Hide ${toggleLabel}` : `Show ${toggleLabel}`}
        >
          {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
    );
  }
);
PasswordInput.displayName = 'PasswordInput';

export { PasswordInput };
